import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { ArrowDownToLine, Wallet } from 'lucide-react';
import { m } from '~/paraglide/messages.js';
import cop from '~/static/icons/cop.png';
import ksm from '~/static/icons/ksm.png';
import usd from '~/static/icons/usd.png';
import { CURRENCIES, getCurrencyKeyFromBalanceCode } from '~/utils/currency';

const ASSETS = {
  DUSD: { title: 'DUSD', icon: usd },
  KSM: { title: 'KSM', icon: ksm },
  COP: { title: 'COP', icon: cop },
} as const;

type Asset = keyof typeof ASSETS;
type AssetPair = `${Asset}/${number}`;
type BalanceInfo = {
  current: string;
  pending: string;
  in?: string;
  out?: string;
};

interface HeroBalanceProps {
  balance?: Record<AssetPair, BalanceInfo>;
  isLoading: boolean;
  selectedAsset: string;
  onChangeAsset?: (asset: AssetPair) => void;
  onTopUp?: () => void;
}

export function HeroBalance({
  balance,
  isLoading,
  selectedAsset,
  onChangeAsset,
  onTopUp,
}: HeroBalanceProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-2">
        <div className="h-10 w-36 animate-pulse rounded-lg bg-muted" />
        <div className="h-5 w-24 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!balance || Object.keys(balance).length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-2">
        <Wallet className="h-8 w-8 text-purple-400/50" />
        <p className="text-sm text-muted-foreground">
          {m['card.account_balance_summary.no_balance_info']()}
        </p>
        {onTopUp && (
          <Button
            variant="outline"
            size="sm"
            onClick={onTopUp}
            className="mt-1 border-purple-500/30 hover:bg-purple-500/10 hover:border-purple-500/50"
          >
            <ArrowDownToLine className="mr-2 h-3.5 w-3.5" />
            {m['card.account_balance_summary.empty_button']()}
          </Button>
        )}
      </div>
    );
  }

  const parsedBalances = Object.entries(balance).map(([pair, info]) => {
    const [assetCode, decimals] = pair.split('/') as [Asset, string];
    const assetMeta = ASSETS[assetCode] || { title: assetCode, icon: ksm };
    const normalizedBalance = +info.current * 10 ** -parseInt(decimals, 10);
    const currencyKey = getCurrencyKeyFromBalanceCode(assetCode);

    const [units, cents] = parseFloat(normalizedBalance.toString())
      .toFixed(2)
      .split('.');

    let formattedAmount: string;
    if (currencyKey) {
      const config = CURRENCIES[currencyKey];
      formattedAmount =
        currencyKey === 'USD'
          ? units
          : Math.floor(normalizedBalance).toLocaleString(config.locale);
    } else {
      formattedAmount = normalizedBalance.toString();
    }

    return {
      pair: pair as AssetPair,
      code: assetCode,
      icon: assetMeta.icon,
      title: assetMeta.title,
      amount: formattedAmount,
      cents: currencyKey === 'USD' ? cents : null,
      suffix: currencyKey ? CURRENCIES[currencyKey].suffix : undefined,
    };
  });

  const activeBalance =
    parsedBalances.find((b) => b.pair === selectedAsset) ?? parsedBalances[0];

  return (
    <div className="flex flex-col items-center gap-3 py-2">
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold tracking-tight">
          ${activeBalance.amount}
        </span>
        {activeBalance.cents && (
          <span className="text-lg text-muted-foreground">
            ,{activeBalance.cents}
          </span>
        )}
        {activeBalance.suffix && (
          <span className="ml-1 text-lg font-semibold text-muted-foreground">
            {activeBalance.suffix}
          </span>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {m['card.account_balance_summary.balance_available']()}
      </p>

      {parsedBalances.length > 1 && (
        <div className="flex items-center gap-1 rounded-full border border-purple-500/20 bg-purple-500/5 p-1">
          {parsedBalances.map((b) => (
            <button
              key={b.pair}
              type="button"
              onClick={() => onChangeAsset?.(b.pair)}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                (selectedAsset === b.pair ||
                  (!selectedAsset && b === parsedBalances[0])) &&
                  'bg-purple-600 text-white shadow-sm',
                selectedAsset !== b.pair &&
                  b !== parsedBalances[0] &&
                  selectedAsset &&
                  'text-muted-foreground hover:text-foreground',
              )}
            >
              <img src={b.icon} alt={b.title} className="h-3.5 w-3.5" />
              {b.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
